import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, LogOut, Mail } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Friendly fallback screen rendered when:
 *   • A logged-in user has no `assigned_modules` and no implicit role access
 *   • An ErrorBoundary trips below the routing layer
 *   • Any other condition where `pickLandingPath()` returns null
 *
 * MUST NEVER render blank or trigger a redirect loop — this is the last line
 * of defence so newly-created users never see a black screen.
 */
export default function NoAccessScreen({ title, message }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FBF8F2] to-[#F0E8DD] px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-stone-200 p-7 sm:p-9 text-center"
        data-testid="no-access-screen">
        <div className="w-14 h-14 mx-auto rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mb-4">
          <ShieldAlert className="w-7 h-7 text-amber-600" strokeWidth={1.75} />
        </div>
        <h1 className="text-lg sm:text-xl font-bold text-[#3A1F12] mb-2">
          {title || 'Access not configured'}
        </h1>
        <p className="text-sm text-[#5C6B64] leading-relaxed mb-6">
          {message ||
            "Your account doesn't have any modules assigned yet. Please ask your administrator to assign the modules you need, then sign back in."}
        </p>

        {user && (
          <div className="mb-6 p-3 bg-stone-50 rounded-lg border border-stone-200 text-left">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-1">Signed in as</div>
            <div className="text-sm font-semibold text-[#1A2421]">{user.name || '—'}</div>
            <div className="text-xs text-stone-500 mt-0.5">{user.email}</div>
            <div className="text-[11px] text-stone-500 mt-1">
              Role: <span className="font-medium text-[#1A2421]">{user.role || '—'}</span>
              {Array.isArray(user.assigned_modules) && user.assigned_modules.length > 0 && (
                <>  ·  {user.assigned_modules.length} module{user.assigned_modules.length !== 1 ? 's' : ''}</>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => navigate('/login')}
            data-testid="no-access-back-to-login"
          >
            Back to sign in
          </Button>
          <Button
            className="flex-1 bg-[color:var(--color-primary)] hover:bg-[#1A4D3C] text-white"
            onClick={() => { logout(); navigate('/login'); }}
            data-testid="no-access-logout"
          >
            <LogOut className="w-3.5 h-3.5 mr-1.5" /> Sign out
          </Button>
        </div>

        <a
          href="mailto:support@restropulse.com"
          className="inline-flex items-center justify-center gap-1.5 text-xs text-[#5C6B64] hover:text-[#3A1F12] mt-5"
        >
          <Mail className="w-3 h-3" /> Need help? Contact support
        </a>
      </div>
    </div>
  );
}
